/**
 * TourPromptCard — first-visit invite. Fires when:
 *   • user lands on any /accounting/* route, AND
 *   • their role is admin/owner/super_admin, AND
 *   • localStorage tour status is "unseen" (never prompted, never dismissed).
 * Non-blocking, bottom-right. Three actions: Start / Not now / Don't show again.
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAccountingTour } from "@/data/accounting/tour/TourContext";
import { useRole } from "@/data/RoleContext";

const ELIGIBLE_ROLES = new Set(["super_admin", "admin", "owner"]);

export function TourPromptCard() {
  const tour = useAccountingTour();
  const { role } = useRole();
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Fire on any in-app route except the bare landing (let it breathe).
    if (pathname === "/login" || pathname === "/auth") { setVisible(false); return; }
    if (!ELIGIBLE_ROLES.has(role)) { setVisible(false); return; }
    if (tour.status !== "unseen") { setVisible(false); return; }
    const t = window.setTimeout(() => {
      setVisible(true);
      tour.markPrompted();
    }, 1200);
    return () => window.clearTimeout(t);
  }, [pathname, role, tour]);

  if (!visible) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[80] w-[min(380px,calc(100vw-2rem))] rounded-lg border border-border bg-card shadow-2xl ring-1 ring-primary/20 animate-in slide-in-from-bottom-4 duration-300">
      <div className="px-4 py-3 flex items-start gap-3">
        <div className="rounded-md bg-primary/15 p-2 shrink-0">
          <Sparkles className="h-4 w-4 text-primary" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold">Take the Koredor Tour</h3>
            <button onClick={() => setVisible(false)} className="text-muted-foreground hover:text-foreground" aria-label="Close">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            Ten chapters covering the full ecosystem — sales → ops → books → year-end. Pick Full, Express (~5 min), or filter by role.
          </p>
        </div>
      </div>
      <div className="px-4 pb-3 flex items-center justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={() => { tour.dismiss(true); setVisible(false); }}>
          Don't show again
        </Button>
        <Button variant="ghost" size="sm" onClick={() => { setVisible(false); }}>
          Not now
        </Button>
        <Button size="sm" onClick={() => { tour.start(); setVisible(false); }}>
          Start tour
        </Button>
      </div>
    </div>
  );
}
